import type { AxiosResponse } from "axios";
import { loadAppConfig, type ServerHost } from "@app/config/app/appConfig";
import { emitSessionLost, OMEGA_TOKEN_EXPIRED_CODE } from "@app/services/session";
import { useAuthStore } from "@app/store/useAuthStore";
import { axiosErrorHandler, assertOmegaResponseOk, getOmegaErrorCode } from "../errors";
import ApiClient, { type RequestConfig } from "../http";

/** Параметры авторизации через OMP API. */
export interface AuthOptions {
    method: "login" | "token";
    login?: string;
    password?: string;
    authToken?: string;
}

interface LoginResponse {
    authToken: string;
}

const omegaErrorHandler = (response: AxiosResponse): AxiosResponse => {
    const errorCode = getOmegaErrorCode(response.data);

    if (errorCode === OMEGA_TOKEN_EXPIRED_CODE) {
        emitSessionLost({
            reason: "token-expired",
            source: "omega-error",
            code: errorCode,
        });
    }

    assertOmegaResponseOk(response.data);
    return response;
};

/**
 * OMP REST API client: reactive baseURL, AuthToken header, Omega error mapping.
 */
class OmpApiClient extends ApiClient {
    private authToken?: string;

    public constructor() {
        super();
        this.registerDefaultInterceptors();
        this.restoreFromPersistedAuth();
    }

    /**
     * Устанавливает токен авторизации для последующих запросов.
     */
    public setAuthToken(token: string | null | undefined): void {
        this.authToken = token ?? undefined;
        useAuthStore.getState().setToken(token ?? null);
        this.applyAuthTokenToInstance(token);
    }

    private applyAuthTokenToInstance(token: string | null | undefined): void {
        this.applyToInstance((inst) => {
            inst.defaults.headers = inst.defaults.headers ?? {};
            if (token == null || token.length === 0) {
                delete inst.defaults.headers.AuthToken;
            } else {
                inst.defaults.headers.AuthToken = token;
            }
        });
    }

    /**
     * Сохраняет сервер в store и обновляет `baseURL` клиента.
     */
    public setServerAsHost(server: ServerHost): void {
        useAuthStore.getState().setServer(server);
        this.setBaseUrl(server.url);
        this.applyAuthTokenToInstance(this.authToken ?? useAuthStore.getState().token);
    }

    private registerDefaultInterceptors(): void {
        this.useResponseInterceptor(omegaErrorHandler);
        this.useErrorInterceptor((error) => {
            axiosErrorHandler(error);
        });
    }

    /**
     * Восстанавливает token и baseURL из persisted auth store при старте приложения.
     */
    public restoreFromPersistedAuth(): void {
        const { token, server } = useAuthStore.getState();
        this.authToken = token ?? undefined;
        this.applyAuthTokenToInstance(token);

        if (server != null) {
            this.setBaseUrl(server.url);
        }
    }

    /**
     * Загружает серверы из config.json.
     */
    public async loadServersFromConfig(byUrl?: string): Promise<ServerHost[]> {
        try {
            const config = await loadAppConfig();

            if (byUrl != null) {
                const found = config.servers.find((item) => item.url === byUrl);
                return found != null ? [found] : [];
            }

            return config.servers;
        } catch {
            throw new Error(
                "Ошибка при чтении конфигурационного файла. Невозможно получить адрес сервера.\nОбратитесь к системному администратору.",
            );
        }
    }

    /**
     * Выбирает сервер: persisted store имеет приоритет, иначе default/first из config.
     */
    public async autoSelectServer(): Promise<void> {
        const serversFromCfg = await this.loadServersFromConfig();
        const { server: persistedServer } = useAuthStore.getState();

        if (serversFromCfg.length === 0) {
            throw new Error(
                "В виртуальном окружение(config) в параметре servers отсутствуют сервара для подключения\n Обратитесь к системному администратору",
            );
        }

        const defaultServer = serversFromCfg.find((item) => item.default === true) ?? serversFromCfg[0];

        if (persistedServer != null) {
            this.setServerAsHost(persistedServer);
        } else {
            this.setServerAsHost(defaultServer);
        }
    }

    /**
     * Получает токен по логину/паролю или принимает готовый token.
     * Загрузка user/labouring выполняется в {@link SessionGate}, не здесь.
     */
    public async authorizeUser(option: AuthOptions): Promise<void> {
        if (option.method === "login" && (option.login == null || option.password == null)) {
            throw new Error("Не указан логин или пароль при авторизации.");
        }
        if (option.method === "token" && option.authToken == null) {
            throw new Error("Не указан токен при авторизации");
        }

        useAuthStore.getState().clearSessionState();
        await this.autoSelectServer();

        let authToken: string;
        if (option.method === "login") {
            const response = await this.post<LoginResponse>(
                "login.getAuthToken",
                {
                    login: option.login,
                    password: option.password,
                },
                { timeout: 3000 },
            );
            authToken = response.authToken;
        } else {
            authToken = String(option.authToken);
        }

        this.setAuthToken(authToken);
    }

    /**
     * POST к Omega API с проверкой токена (по умолчанию) и поддержкой `RequestConfig`.
     */
    public postOmega<T = unknown, D = unknown>(
        targetUri: string,
        targetParams?: D,
        config?: RequestConfig & { skipAuth?: boolean },
    ): Promise<T> {
        const { skipAuth = false, ...requestConfig } = config ?? {};

        if (!skipAuth) {
            const { token } = useAuthStore.getState();
            if (token == null || token.length === 0) {
                throw new Error("Пройдите авторизацию");
            }
            this.setAuthToken(token);
        }

        return this.post<T, D>(targetUri, targetParams, requestConfig);
    }
}

/** Единый экземпляр OMP API client. */
export const ompApiClient = new OmpApiClient();

export type { RequestConfig };
